import type { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@store/store";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import {
	EMBED_PORTAL_QUERY_KEY,
	EMBED_PORTAL_QUERY_VALUE,
	isEmbedPortalSearch,
} from "@constants/embedPortal.config";
import {
	clearPostLoginRedirect,
	setEmbedAlternateChrome,
	setPostLoginRedirect,
} from "@store/slices/externalPortalSlice";

interface AuthGuardProps {
	children: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
	const { isAuthenticated } = useSelector((state: RootState) => state.user);
	const location = useLocation();
	const dispatch = useDispatch<AppDispatch>();

	if (!isAuthenticated) {
		// Check if OTP verification is pending from signup
		const otpVerificationSource = localStorage.getItem("otpVerificationSource");
		const otpEmail = localStorage.getItem("otpEmail");
		
		// If OTP verification is pending, redirect to OTP page instead of login
		if (otpVerificationSource === "signup" && otpEmail) {
			return <Navigate to={BROWSER_ROUTES.VERIFY_OTP} replace />;
		}
		
		if (isEmbedPortalSearch(location.search)) {
			const intended = `${location.pathname}${location.search}${location.hash}`;
			dispatch(setEmbedAlternateChrome(true));
			dispatch(setPostLoginRedirect(intended));
			const search = `?${EMBED_PORTAL_QUERY_KEY}=${EMBED_PORTAL_QUERY_VALUE}&redirect=${encodeURIComponent(intended)}`;
			return <Navigate to={{ pathname: BROWSER_ROUTES.LOGIN, search }} state={{ from: location }} replace />;
		}
		dispatch(clearPostLoginRedirect());
		return <Navigate to={BROWSER_ROUTES.LOGIN} state={{ from: location }} replace />;
	}
	return <>{children}</>;
};

export default AuthGuard;
